import { useState } from "react";
import { useProjectMutation } from "../migration/project-mutation";
import "./vocal-moments.css";

export interface VocalMoment {
  id: string; lyric_segment_ids: string[]; section: string; why: string; story_role: string;
}
export interface VocalMomentsModel {
  projectId: string; binding: string; disabled: boolean; voiceApproved: boolean;
  singers: {id: string; name: string}[];
  lines: {id: string; display_text?: string; normalized_text?: string; start_ms: number; end_ms: number; voice_id?: string}[];
  shots: {id: string; position: number; lyric_ids: string[]}[];
  plan?: {revision: number; singer_id: string; moments: VocalMoment[]; warnings?: string[]; approved?: boolean; valid?: boolean; provider?: string; model?: string};
  submit: (kind: "save" | "approve" | "propose", data: Record<string, unknown>) => Promise<void>;
  /** One bokeh choice for the whole production: free in the picture recipe of these close-ups. */
  bokeh?: boolean | null;
  setBokeh?: (on: boolean) => Promise<void> | void;
}

/** The reason recorded when the maker simply keeps Iris's choice. */
export const KEEP_IRIS_CHOICE = "Kept Iris's proposal after review";
const NO_SINGING = "No singing on camera in this film";

export function VocalMomentsPanel({model: m, onApproved}: {model?: VocalMomentsModel; onApproved?: () => Promise<void> | void}) {
  if (!m) return null;
  return <VocalMomentsEditor key={`${m.projectId}:${m.plan?.revision || 0}`} model={m} onApproved={onApproved} />;
}

function clock(ms: number) {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function VocalMomentsEditor({model: m, onApproved}: {model: VocalMomentsModel; onApproved?: () => Promise<void> | void}) {
  const mutation = useProjectMutation("vocal-moments", m.projectId);
  const [singer, setSinger] = useState(m.plan?.singer_id || m.singers[0]?.id || "");
  const [moments, setMoments] = useState<VocalMoment[]>(m.plan?.moments || []);
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [noSinging, setNoSinging] = useState(false);
  const disabled = m.disabled || mutation.pending;
  const singerName = m.singers.find(item => item.id === singer)?.name || "your singer";
  const approved = !!m.plan?.approved;
  const lineOf = (id: string) => m.lines.find(item => item.id === id);
  const text = (ids: string[]) => ids.map(id => {
    const line = lineOf(id);
    return line?.display_text || line?.normalized_text || id;
  }).join(" / ");
  const span = (ids: string[]) => {
    const lines = ids.map(lineOf).filter(Boolean) as VocalMomentsModel["lines"];
    if (!lines.length) return "";
    const start = Math.min(...lines.map(line => line.start_ms)), end = Math.max(...lines.map(line => line.end_ms));
    return `${clock(start)} · ${((end - start) / 1000).toFixed(1)} s`;
  };
  const add = (ids: string[], role = "Singer addresses the viewer directly") => {
    if (!ids.length) return;
    setMoments([...moments, {id: `vm_${crypto.randomUUID()}`, lyric_segment_ids: ids,
      section: "maker selection", why: reason || "Maker-selected vocal moment", story_role: role}]);
    setSelected([]); setNoSinging(false);
  };
  const payload = (kind: "save" | "approve", options: {none?: boolean; why?: string} = {}) => ({
    expected_revision: m.plan?.revision || 0, binding: m.binding, singer_id: singer,
    action: kind, moments: options.none ? [] : moments, confirm_no_singing: !!options.none || noSinging,
    reason: (options.why || reason).trim() || KEEP_IRIS_CHOICE,
  });
  const propose = () => {
    if (!window.confirm("Ask Iris to pick the singing moments? Your brief and lyrics are sent to the story model (a few cents of text). Nothing is generated or approved automatically.")) return;
    void mutation.run(() => m.submit("propose", {expected_revision: m.plan?.revision || 0, binding: m.binding, singer_id: singer, confirm_remote: true}));
  };
  // Keeping the moments is the one decision here; building the storyboard follows by itself.
  const approve = (options: {none?: boolean; why?: string} = {}) => void mutation.run(async () => {
    await m.submit("approve", payload("approve", options));
    await onApproved?.();
  });
  const save = () => void mutation.run(() => m.submit("save", payload("save")));

  if (approved) return <section className="creative-section vocal-moments" aria-label="Vocal moments before the storyboard">
    <h3>{m.plan?.moments.length ? `${m.plan.moments.length} singing moments approved` : "No singing on camera"}</h3>
    <p>{m.plan?.moments.length ? `Only these lines show ${singerName} singing in close-up; every other shot tells the story.` : "Every shot tells the story; nobody sings on camera."} Changing them later means a free rebuild of the storyboard.</p>
  </section>;

  return <section className="creative-section vocal-moments" aria-label="Vocal moments before the storyboard">
    <h3>{moments.length ? `Iris picked ${moments.length} moments where ${singerName} sings on camera` : "Which lines are sung on camera?"}</h3>
    <p>Only these shots become singing close-ups with lip-sync; all other shots tell the story with the mouth closed. Nothing is generated here.</p>
    {!m.voiceApproved && <p role="alert">First confirm who sings each line in the song analysis (Voice Map). Iris never guesses the singer.</p>}
    {!!m.plan?.warnings?.length && <ul className="vocal-moment-warnings" role="status">{m.plan.warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>}
    {!!moments.length && <ol className="vocal-moment-cards">{moments.map((moment, index) => <li key={moment.id} className="vocal-moment-card">
      <blockquote>“{text(moment.lyric_segment_ids)}”</blockquote>
      <small>{[span(moment.lyric_segment_ids), moment.section].filter(Boolean).join(" · ")}</small>
      {moment.why && <p>{moment.why}</p>}
      <button type="button" className="btn ghost" disabled={disabled} onClick={() => setMoments(moments.filter((_, i) => i !== index))}>Leave out</button>
    </li>)}</ol>}
    {!!moments.length && m.setBokeh && <label className="vocal-moment-bokeh">
      <input type="checkbox" checked={!!m.bokeh} disabled={disabled} onChange={event => void m.setBokeh?.(event.target.checked)} />
      <span>Soft bokeh behind your singer in these close-ups <small>· free now, the pictures are made with it</small></span>
    </label>}
    {!!moments.length && m.setBokeh && m.bokeh && <p className="vocal-moment-bokeh-on" role="status">
      Bokeh is on: {moments.length === 1 ? "this close-up gets it when its picture is made" : `these ${moments.length} close-ups get it when their pictures are made`}. Nothing is charged now; you can change it later under Singing.
    </p>}
    <div className="vocal-moment-actions">
      {moments.length
        ? <button type="button" className="btn primary" disabled={disabled || !m.voiceApproved || !singer} onClick={() => approve()}>
            {onApproved ? `Use these ${moments.length} moments and build the storyboard` : `Use these ${moments.length} moments`}
          </button>
        : <button type="button" className="btn primary" disabled={disabled || !m.voiceApproved || !singer} onClick={propose}>Let Iris choose the singing moments</button>}
      {!!moments.length && <button type="button" className="btn" disabled={disabled || !m.voiceApproved || !singer} onClick={propose}>Let Iris choose again</button>}
      <button type="button" className="btn ghost" disabled={disabled || !m.voiceApproved || !singer} onClick={() => approve({none: true, why: NO_SINGING})}>No singing on camera</button>
    </div>
    <details className="vocal-moment-advanced">
      <summary>Edit the moments yourself</summary>
      <fieldset disabled={disabled}>
        <label>Solo singer <select aria-label="Vocal moment singer" value={singer} onChange={event => {setSinger(event.target.value); setMoments([]); setSelected([]);}}>
          {m.singers.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select></label>
        <ol>{moments.map((moment, index) => <li key={moment.id}>
          <p>{text(moment.lyric_segment_ids)}</p>
          <label>Section <input aria-label={`Section ${index + 1}`} value={moment.section} onChange={e => setMoments(moments.map((item, i) => i === index ? {...item, section: e.target.value} : item))} /></label>
          <label>Why this moment <input aria-label={`Why moment ${index + 1}`} value={moment.why} onChange={e => setMoments(moments.map((item, i) => i === index ? {...item, why: e.target.value} : item))} /></label>
          <label>Story role <input aria-label={`Story role ${index + 1}`} value={moment.story_role} onChange={e => setMoments(moments.map((item, i) => i === index ? {...item, story_role: e.target.value} : item))} /></label>
        </li>)}</ol>
        <p>Add lyric lines as a moment:</p>
        {m.lines.filter(line => line.voice_id === singer).map(line => <label key={line.id} style={{display: "block"}}>
          <input type="checkbox" checked={selected.includes(line.id)} onChange={e => setSelected(e.target.checked ? [...selected, line.id] : selected.filter(id => id !== line.id))} />
          {text([line.id])} ({((line.end_ms - line.start_ms) / 1000).toFixed(1)} s)
        </label>)}
        <button type="button" className="btn" disabled={!selected.length} onClick={() => add(selected)}>Add selected lines as one moment</button>
        {m.shots.filter(shot => shot.lyric_ids.length && shot.lyric_ids.every(id => m.lines.some(line => line.id === id && line.voice_id === singer))).map(shot =>
          <button type="button" className="btn" key={shot.id} onClick={() => add(shot.lyric_ids, `Maker override: rebuild shot ${shot.position + 1} as a frontal singing close-up`)}>Add shot {shot.position + 1} as a vocal moment</button>
        )}
        <label>Note for your own record (optional) <input aria-label="Vocal moment decision reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={1000} /></label>
        {!moments.length && <label><input type="checkbox" checked={noSinging} onChange={e => setNoSinging(e.target.checked)} />No visible singing anywhere in this production</label>}
        <button type="button" className="btn" disabled={!singer} onClick={save}>Save as draft</button>
      </fieldset>
    </details>
    {m.plan?.provider && <p className="subtle">Proposed by Iris · revision {m.plan.revision}. Saving preserves all images and clips.</p>}
    {mutation.error && <p role="alert">{mutation.error}</p>}
  </section>;
}
